// Client IP and country for audit logs, same approach as the KPro UI (audit.log.service / geo-country.service).
// IP-to-country lookup table (public/assets/geoip) is DB-IP's "IP to Country Lite" dataset,
// licensed under CC BY 4.0 - https://db-ip.com/db/lite.php. Attribution required on redistribution.
import { publicIpv4 } from 'public-ip';

const GEOIP_RANGES_URL = `${process.env.PUBLIC_URL || ''}/assets/geoip/ip-country-ranges.txt`;
const UNKNOWN = 'Unknown';

let cachedIpAddress = '';
let ipPromise: Promise<string> | null = null;
let ends: Uint32Array | null = null;
let codes = '';
let loadPromise: Promise<void> | null = null;

export const getPublicIpAddress = async (): Promise<string> => {
  if (cachedIpAddress) {
    return cachedIpAddress;
  }
  if (!ipPromise) {
    // Share one in-flight lookup between the page-load prefetch and the audit call
    ipPromise = publicIpv4({ timeout: 2000 })
      .then(ip => {
        cachedIpAddress = ip;
        return ip;
      })
      .finally(() => {
        ipPromise = null;
      });
  }
  return ipPromise;
}

const loadRanges = async () => {
  const response = await fetch(GEOIP_RANGES_URL);
  if (!response.ok) {
    throw new Error(`geoip range table request failed with status ${response.status}`);
  }

  const text = await response.text();
  const lines = text.split(/\r?\n/).filter(line => line.length > 0);

  // Guard against the SPA fallback handing us index.html with a 200 status:
  // every real line is "<base36-range-end> <ISO-3166 alpha-2 code>".
  if (lines.length === 0 || !/^[0-9a-z]+ [A-Z]{2}$/.test(lines[0])) {
    throw new Error('geoip range table response was not the expected data file');
  }

  const loadedEnds = new Uint32Array(lines.length);
  const loadedCodes = new Array<string>(lines.length);
  for (let i = 0; i < lines.length; i++) {
    const spaceIndex = lines[i].indexOf(' ');
    loadedEnds[i] = parseInt(lines[i].substring(0, spaceIndex), 36);
    loadedCodes[i] = lines[i].substring(spaceIndex + 1);
  }
  ends = loadedEnds;
  codes = loadedCodes.join('');
}

const ensureLoaded = () => {
  if (ends) {
    return Promise.resolve();
  }
  if (!loadPromise) {
    // Retry on the next call if this attempt fails
    loadPromise = loadRanges().catch(error => {
      loadPromise = null;
      throw error;
    });
  }
  return loadPromise;
}

const ipToUint32 = (ip: string): number | null => {
  const parts = ip.split('.');
  if (parts.length !== 4) {
    return null; // IPv6 or malformed input is not covered by this table
  }
  let value = 0;
  for (const part of parts) {
    const octet = Number(part);
    if (!Number.isInteger(octet) || octet < 0 || octet > 255) {
      return null;
    }
    value = (value * 256) + octet;
  }
  return value >>> 0;
}

// Ranges are cumulative "end" boundaries covering the full IPv4 space,
// so the country is whichever range's end is the first one >= target.
const findRangeIndex = (rangeEnds: Uint32Array, target: number) => {
  let low = 0;
  let high = rangeEnds.length - 1;
  while (low < high) {
    const mid = (low + high) >>> 1;
    if (rangeEnds[mid] < target) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return low;
}

export const resolveCountry = async (ipAddress: string): Promise<string> => {
  const target = ipToUint32(ipAddress);
  if (target === null) {
    return UNKNOWN;
  }
  try {
    await ensureLoaded();
  } catch (error) {
    console.error('Unable to load geoip range table', error);
    return UNKNOWN;
  }
  if (!ends) {
    return UNKNOWN;
  }

  const index = findRangeIndex(ends, target);
  const countryCode = codes.substring(index * 2, index * 2 + 2);
  if (countryCode.length !== 2 || countryCode === 'ZZ') {
    return UNKNOWN;
  }

  try {
    const regionNames = new (Intl as any).DisplayNames(['en'], { type: 'region' });
    return regionNames.of(countryCode) ?? countryCode;
  } catch {
    return countryCode;
  }
}

export const getClientLocation = async () => {
  let ipAddress = '';
  let country = '';
  try {
    ipAddress = await getPublicIpAddress();
    country = await resolveCountry(ipAddress);
  } catch (error) {
    console.error('Unable to resolve client IP address for audit log', error);
  }
  return { ipAddress, country };
}

/**
 * Warm up the IP lookup and the geoip range table (~4 MB) while the user is still
 * on the login page, so the login audit does not have to wait for them.
 */
export const prefetchClientLocation = () => {
  getClientLocation();
}
